import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { buildInvoiceHtmlDocument } from './vendorInvoiceHtml';
import { imageUrlToDataUrl, inlineImagesInElement } from './imageToDataUrl';

/**
 * Rasterize a DOM node to a multi-page A4 PDF and trigger download.
 * Logos are inlined as data URLs first so S3/CORS never blanks them.
 */
export async function downloadInvoicePdfFromElement(element, filename = 'invoice.pdf') {
  if (!element) throw new Error('Nothing to export');

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:fixed;left:-10000px;top:0;width:794px;background:#fff;pointer-events:none;opacity:0;z-index:-1;';
  const clone = element.cloneNode(true);
  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    await inlineImagesInElement(clone);

    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      imageTimeout: 15000,
    });

    const imgData = canvas.toDataURL('image/png', 1.0);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(filename);
  } finally {
    if (host.parentNode) host.parentNode.removeChild(host);
  }
}

/**
 * Build standalone invoice HTML in an off-screen iframe and export to PDF (for list view without live preview).
 */
export async function downloadInvoicePdfFromInvoiceData(invoice, vendorProfile) {
  const vp = vendorProfile || {};
  const rawLogo = invoice?.logo_url || vp.logo_url || vp.brand_settings?.logo_url || null;
  const logoDataUrl = rawLogo ? await imageUrlToDataUrl(rawLogo) : null;

  const html = buildInvoiceHtmlDocument(
    { ...invoice, logo_url: logoDataUrl || rawLogo },
    {
      ...vp,
      logo_url: logoDataUrl || vp.logo_url,
      brand_settings: {
        ...(vp.brand_settings || {}),
        logo_url: logoDataUrl || vp.brand_settings?.logo_url,
      },
    }
  );
  const filename = `invoice-${invoice.invoice_number || invoice.id || 'export'}.pdf`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = 'invoice-pdf-export';
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:794px;min-height:1123px;border:0;margin:0;padding:0;opacity:0;pointer-events:none;background:#fff';

  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('Invoice frame has no document');
    doc.open();
    doc.write(html);
    doc.close();

    const body = doc.body;
    if (!body) throw new Error('Invoice frame has no body');
    const root = doc.querySelector('.invoice-sheet') || body;

    await inlineImagesInElement(root);
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 80)));

    const canvas = await html2canvas(root, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      imageTimeout: 15000,
    });

    const imgData = canvas.toDataURL('image/png', 1.0);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(filename);
  } finally {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}
